import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { ApiService } from '../services/api';
import { PlanogramComplianceConfigItem } from '../types';

export function configItemsToRecord(
  items: PlanogramComplianceConfigItem[]
): Record<string, unknown> {
  return items.reduce(
    (acc, item) => {
      acc[item.key] = item.value;
      return acc;
    },
    {} as Record<string, unknown>
  );
}

export const usePlanogramComplianceConfig = (imageId: number | null | undefined) => {
  const { token } = useAuthContext();
  const [config, setConfig] = useState<PlanogramComplianceConfigItem[] | null>(null);
  const [defaultConfig, setDefaultConfig] = useState<PlanogramComplianceConfigItem[] | null>(null);
  const fetchedImageIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!imageId || !token) return;
    if (fetchedImageIdRef.current === imageId) return;

    ApiService.fetchPlanogramComplianceConfigs(token, imageId)
      .then((items) => {
        fetchedImageIdRef.current = imageId;
        setDefaultConfig(items);
        setConfig(items);
      })
      .catch((err) => {
        console.error('Error fetching planogram compliance configs:', err);
      });
  }, [imageId, token]);

  const updateConfig = useCallback((key: string, value: number | boolean | string) => {
    setConfig((prev) =>
      prev ? prev.map((item) => (item.key === key ? { ...item, value } : item)) : prev
    );
  }, []);

  const resetConfig = useCallback(() => {
    setDefaultConfig((current) => {
      if (current) {
        setConfig(current);
      }
      return current;
    });
  }, []);

  return {
    config,
    defaultConfig,
    updateConfig,
    resetConfig,
  };
};
